import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";

const TIERS = [
  {
    id: "clinic",
    name: "Clinic",
    price: "$490",
    unit: "per radiologist / month",
    blurb: "For private practices and small groups adding AI triage to daily reads.",
    features: [
      "Up to 500 studies per month",
      "X-Ray + CT analysis",
      "Worklist prioritization",
      "Explainable heatmaps on every flag",
      "BAA included",
      "Email support",
    ],
    cta: "Start a pilot",
    href: "/register",
    featured: false,
  },
  {
    id: "imaging-center",
    name: "Imaging Center",
    price: "$2,900",
    unit: "per site / month",
    blurb: "For high-volume centers running multi-modality oncology screening.",
    features: [
      "Unlimited studies",
      "All 4 modalities — X-Ray, CT, MRI, Ultrasound",
      "Oncology detection module",
      "PACS / RIS integration",
      "Team analytics dashboard",
      "Priority support with 4h response",
    ],
    cta: "Start a pilot",
    href: "/register",
    featured: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "Custom",
    unit: "annual agreement",
    blurb: "For hospital groups and IDNs with strict security and scale needs.",
    features: [
      "On-premise or single-tenant VPC deployment",
      "SSO / SAML and role-based access",
      "Custom model tuning on your priors",
      "99.9% uptime SLA",
      "Security review packet + pen test summaries",
      "Dedicated clinical success team",
    ],
    cta: "Talk to sales",
    href: "#contact",
    featured: false,
  },
];

export default function Pricing() {
  return (
    <section id="pricing" className="bg-white py-24 md:py-32 border-t border-slate-200" data-testid="pricing-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs uppercase tracking-[0.3em] text-brand mb-6"
            >
              Pricing
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[0.95]"
            >
              Priced for every
              <br />
              <span className="text-mute">reading room.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="max-w-sm text-mute text-base leading-relaxed"
          >
            Every plan starts with a 30-day pilot on your own studies. No hardware, no long contracts, cancel anytime.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className={`relative flex flex-col rounded-2xl border p-8 md:p-10 transition-[box-shadow,transform,border-color] duration-500 hover:-translate-y-1 ${
                t.featured
                  ? "bg-[#0F172A] text-white border-[#0F172A] shadow-[0_16px_50px_rgba(15,23,42,0.18)]"
                  : "bg-white border-slate-200 hover:shadow-[0_4px_20px_rgba(15,23,42,0.06)]"
              }`}
              data-testid={`pricing-card-${t.id}`}
            >
              {t.featured && (
                <span className="absolute -top-3.5 left-8 font-mono text-[10px] uppercase tracking-[0.2em] bg-brand text-white px-3 py-1.5 rounded-full" data-testid="pricing-featured-badge">
                  Most popular
                </span>
              )}
              <p className={`font-mono text-xs uppercase tracking-[0.25em] mb-6 ${t.featured ? "text-sky-400" : "text-mute"}`}>
                {t.name}
              </p>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter">{t.price}</span>
              </div>
              <p className={`font-mono text-[11px] uppercase tracking-[0.15em] mb-6 ${t.featured ? "text-slate-400" : "text-mute"}`}>
                {t.unit}
              </p>
              <p className={`text-sm leading-relaxed mb-8 ${t.featured ? "text-slate-300" : "text-mute"}`}>{t.blurb}</p>

              <ul className="space-y-3.5 mb-10 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <span className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center mt-0.5 ${t.featured ? "bg-brand/20 text-sky-400" : "bg-brand/10 text-brand"}`}>
                      <Check className="w-3 h-3" />
                    </span>
                    <span className={t.featured ? "text-slate-200" : "text-slate-700"}>{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href={t.href}
                data-testid={`pricing-cta-${t.id}`}
                className={`flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 rounded-full transition-colors duration-300 ${
                  t.featured
                    ? "bg-brand text-white hover:bg-white hover:text-[#0F172A]"
                    : "bg-white border border-slate-300 text-slate-900 hover:bg-[#0F172A] hover:text-white hover:border-[#0F172A]"
                }`}
              >
                {t.cta} <ArrowUpRight className="w-4 h-4" />
              </a>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-mute mt-12"
          data-testid="pricing-footnote"
        >
          All plans include HIPAA-aligned infrastructure and a signed BAA
        </motion.p>
      </div>
    </section>
  );
}
